"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { isBackendPath } from "@/shared/lib/backend-paths";

const LINK_CLASS = "text-link underline underline-offset-[2px] hover:text-ink";

const PRIVATE_LINKS = [
  { href: "/privat", label: "Privatrengøring" },
  { href: "/rengoering/koebenhavn", label: "Rengøring København" },
  { href: "/rengoering/aarhus", label: "Rengøring Aarhus" },
  { href: "/rengoering/odense", label: "Rengøring Odense" },
  { href: "/rengoering/aalborg", label: "Rengøring Aalborg" },
  { href: "/rengoering/frederiksberg", label: "Rengøring Frederiksberg" },
  { href: "/rengoering", label: "Alle byer og regioner" },
  { href: "/guides", label: "Guides" },
  { href: "/ordliste", label: "Ordliste" },
];

const BUSINESS_LINKS = [
  { href: "/erhverv", label: "Erhvervsrengøring" },
  { href: "/firmaer", label: "Rengøringsfirmaer" },
  { href: "/bliv-partner", label: "Bliv partner" },
  { href: "/partner/login", label: "Partner-login" },
];

const ABOUT_LINKS = [
  { href: "/saadan-virker-det", label: "Sådan virker det" },
  { href: "/saadan-virker-det/ai-assistent", label: "Brug en AI-assistent" },
  { href: "/priser", label: "Priser" },
  { href: "/nyheder", label: "Nyheder og artikler" },
  { href: "/faq", label: "FAQ" },
  { href: "/kontakt", label: "Kontakt" },
  { href: "/om-os", label: "Om os" },
  { href: "/redaktionel-politik", label: "Redaktionel politik" },
  { href: "/metodologi", label: "Metodologi" },
  { href: "/privatlivspolitik", label: "Privatlivspolitik" },
];

function FooterColumn({
  heading,
  links,
}: {
  heading: string;
  links: { href: string; label: string }[];
}) {
  return (
    <div>
      <p className="mb-2.5 text-sm font-medium text-ink">{heading}</p>
      <ul className="space-y-1.5 text-sm leading-relaxed">
        {links.map((link) => (
          <li key={link.href}>
            <Link href={link.href} className={LINK_CLASS}>
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Footer() {
  const pathname = usePathname();

  if (isBackendPath(pathname)) {
    return null;
  }

  return (
    <footer className="border-t border-border bg-surface px-6 py-12">
      <div className="mx-auto grid max-w-page gap-10 md:grid-cols-2 lg:grid-cols-[1.3fr_1fr_1fr_1fr]">
        <div>
          <Link href="/" className="block font-serif text-base leading-none tracking-[-0.01em]">
            <span className="font-bold text-theme-accent">rengøring</span>
            <span className="font-normal text-ink">idanmark.dk</span>
          </Link>
          <p className="mt-3 text-sm leading-relaxed text-ink-muted">
            En uafhængig ressource der hjælper danske virksomheder og private med at finde
            rengøring.
          </p>
          <p className="mt-3 text-sm text-ink-muted">Montem Digital ApS · CVR 42678651</p>
          <p className="mt-1 text-sm">
            <a href="mailto:info@rengoeringidanmark.dk" className={LINK_CLASS}>
              info@rengoeringidanmark.dk
            </a>
          </p>
          <p className="mt-1 text-sm">
            <a href="tel:+4591309013" className={LINK_CLASS}>
              91 30 90 13
            </a>
          </p>
        </div>

        <FooterColumn heading="For private" links={PRIVATE_LINKS} />
        <FooterColumn heading="For virksomheder" links={BUSINESS_LINKS} />
        <FooterColumn heading="Om os" links={ABOUT_LINKS} />
      </div>

      <div className="mx-auto mt-8 max-w-page border-t border-border pt-4 text-xs text-ink-subtle">
        © {new Date().getFullYear()} Montem Digital ApS ·{" "}
        <Link href="/privatlivspolitik" className={LINK_CLASS}>
          Privatlivspolitik
        </Link>
      </div>
    </footer>
  );
}
